import { useState, useEffect, useCallback, useRef } from 'react';
import * as _ from 'lodash';
import { IoTimeOutline, IoChevronDown, IoChevronForward, IoSearchOutline, IoCloseCircleOutline } from 'react-icons/io5';
import { MdViewModule, MdViewList } from 'react-icons/md';
import { CircleQuestionMark } from 'lucide-react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import Chip from '@mui/material/Chip';
import { MdUploadFile } from 'react-icons/md';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import FileTypeFrame from '@CommonFunctionalDisplay/FileTypeFrame';
import FileRow from '@CommonFunctionalDisplay/FileRow';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useFileUploadHook } from '@Hooks/useFileUploadHook';
import FileService from '@Helpers/URLHelpers/FileService';
import ErrorHandler from '@Root/ErrorHandler';
import './RecentItems.scss';

export default function RecentItems(props) {

    const [cursor, setCursor] = useState('auto');
    const [loading, setLoading] = useState(false);
    const [searchActive, setSearchActive] = useState(false);
    const [userList, setUserList] = useState([]);
    const [thisWeekFiles, setThisWeekFiles] = useState([]);
    const [lastMonthFiles, setLastMonthFiles] = useState([]);
    const [lifeTimeFiles, setLifeTimeFiles] = useState([]);
    const [recentFileList, setRecentFileList] = useState([]);
    const [viewType, setViewType] = useState('grid');
    const [files, setFiles] = useState(null);
    const inputRef = useRef();

    const [expandedSections, setExpandedSections] = useState({
        search: true,
        thisWeek: true,
        lastMonth: false,
        lifeTime: false
    });

    const { currentLeadsId } = useReduxLeads();
    const { filesCount, fetchFileStorage, currentFiles } = useReduxFileStorage();
    const { currentProjectId } = useReduxProject();
    const { activeUserId } = useReduxActiveUser();
    const defaultFilters = {
        filter: {
            searchQuery: '',
            sortBy: 'createTimeStamp',
            sortAsc: false,
            projectIdList: [currentProjectId],
            fileStoreTypeList: [],
            tableTypeList: [],
            userIdList: [],
            createTimeStampTo: 0,
            createTimeStampFrom: 0
        },
        activeUserId: activeUserId
    };
    const [filters, setFilters] = useState(_.cloneDeep(defaultFilters));
    const { validate, fileType, dimensions } = useFileUploadHook();
    const fileService = new FileService();
    const errorHandler = new ErrorHandler();
    const className = 'recentItems';

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        setLoading(true);
        await fetchFileStorage('LEADS_WHATSAPP', currentLeadsId);
        setLoading(false);
    };

    const handleViewTypeChange = (type) => {
        setViewType(type);
    };

    const handleFileChange = async (event) => {
        setCursor('wait');
        const file = event.target.files[0];
        if (!!file) {
            const result = await validate(file);
            if(result.isValid) {
                let filesObj = { ...files };
                const url = await convertFileToBase64(file);
                let newFileObj = {
                    file: url,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    projectId: currentProjectId
                }
                await handleSubmit(newFileObj)
            } else {
                SnackbarManager.show(result.error, 'error');
            }
        }
        setCursor('auto');
    };

    const handleSubmit = async (newFileObj) => {
        setCursor('wait');
        let file = newFileObj;
        try {
            let body = {
                'fileName': file.fileName,
                'tableType': 'LEADS_WHATSAPP',
                'itemId': currentLeadsId,
                'file': file,
                'projectId': currentProjectId,
                'userId': activeUserId,
                'storeType': 'whatsappFile'
            }
            let response = await fileService.createFileForLeads(body);
            if(response.status < 400) {
                SnackbarManager.show('File Added', 'success');
                await renderData();
            } else {
                SnackbarManager.show(response.message, 'error');
            }
        } catch (err) {
            SnackbarManager.show('Something went wrong', 'error');
            errorHandler.handleError(err);
        }
        inputRef.current.value = '';
        setCursor('auto');
    }

    const handleChoose = (event) => {
        inputRef.current.click();
    };

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const WhatsAppUploadRules = () => {
        return (
            <div className="waUploadRules">
                <h3 className="waUploadRules__title">
                    WhatsApp File Upload Rules
                </h3>
                <div className="waUploadRules__section">
                    <h4 className="waUploadRules__subtitle">📷 Image</h4>
                    <ul className="waUploadRules__list">
                        <li>Formats: JPG, JPEG, PNG</li>
                        <li>Maximum size: 5 MB</li>
                        <li>Minimum dimensions: 500 x 500 px</li>
                        <li>Recommended aspect ratio: 1.91:1</li>
                    </ul>
                </div>
                <div className="waUploadRules__section">
                    <h4 className="waUploadRules__subtitle">🎥 Video</h4>
                    <ul className="waUploadRules__list">
                        <li>Formats: MP4, 3GP</li>
                        <li>Maximum size: 16 MB</li>
                    </ul>
                </div>
                <div className="waUploadRules__section">
                    <h4 className="waUploadRules__subtitle">📄 Document</h4>
                    <ul className="waUploadRules__list">
                        <li>Format: PDF</li>
                        <li>Maximum size: 100 MB</li>
                    </ul>
                </div>
            </div>
        );
    };

    return (
        <Box className={className} style={{ cursor: cursor }}>
            <Box className={className + '__header'}>
                <Box className={className + '__header__left'}>
                    <IoTimeOutline size={18} className={className + '__header__icon'} />
                    <Box>
                        <Typography  className={className + '__header__title'}>
                            Recent Files
                        </Typography>
                        <Typography variant="caption" className={className + '__header__subtitle'}>
                            {filesCount} {filesCount === 1 ? 'file' : 'files'} found
                        </Typography>
                    </Box>
                </Box>
                <Box className={className + '__header__right'}>
                    <div
                        onClick={handleChoose}
                        className={className + '__menu__item upload_btn flex-row-center-0p5vw-gap'}>
                        <div className={className + '__menu__item__icon'}>
                            <MdUploadFile />
                        </div>
                        <ListItemText
                            primary="Upload File"
                            primaryTypographyProps={{
                                className: className + '__menu__item__primary'
                            }}
                            secondaryTypographyProps={{
                                className: className + '__menu__item__secondary'
                            }}
                        />
                    </div>
                    <Tooltip title={WhatsAppUploadRules()} placement="bottom-start">
                        <CircleQuestionMark size={18}/>
                    </Tooltip>
                        <input
                            ref={inputRef}
                            type='file'
                            accept="application/pdf, text/plain, image/jpeg, image/jpe, image/png, video/mp4, video/3gpp"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                    <Box className={className + '__viewToggle'}>
                        <Tooltip title="Grid View" arrow placement="bottom" slots={{ transition: Zoom }}>
                            <IconButton 
                                onClick={() => handleViewTypeChange('grid')}
                                className={`${className}__viewBtn ${viewType === 'grid' ? 'active' : ''}`}
                                size="small">
                                <MdViewModule />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="List View" arrow placement="bottom" slots={{ transition: Zoom }}>
                            <IconButton 
                                onClick={() => handleViewTypeChange('list')}
                                className={`${className}__viewBtn ${viewType === 'list' ? 'active' : ''}`}
                                size="small">
                                <MdViewList />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>
            {loading && (
                <Box className={className + '__loading'}>
                    <CircularProgress size={40} />
                    <Typography variant="body2">Loading recent files...</Typography>
                </Box>
            )}
            {!loading && (
                <Box className={className + '__content'}>
                    <Box className={className + '__section'}>
                        <Box className={viewType === 'grid' ? className + '__grid' : className + '__list'}>
                            {Object.keys(currentFiles || {})?.map((fileId, index) => (
                                viewType === 'grid' ? (
                                    <FileTypeFrame
                                        key={index}
                                        fileId={fileId}
                                        fileData={currentFiles[fileId]}
                                        fileType={currentFiles[fileId]?.fileType}
                                        fileName={currentFiles[fileId]?.fileName}
                                        previewImgUrl={currentFiles[fileId]?.previewImgUrl}
                                        viewType={viewType}
                                        renderData={renderData}/>
                                ) : (
                                    <FileRow
                                        key={index}
                                        fileId={fileId}
                                        fileData={currentFiles[fileId]}
                                        fileType={currentFiles[fileId]?.fileType}
                                        fileName={currentFiles[fileId]?.fileName}
                                        previewImgUrl={currentFiles[fileId]?.previewImgUrl}
                                        renderData={renderData}/>
                                )
                            ))}
                        </Box>
                    </Box>
                    {filesCount === 0 && (
                        <Box className={className + '__empty'}>
                            <IoTimeOutline className={className + '__empty__icon'} />
                            <Typography variant="h6" className={className + '__empty__title'}>
                                No recent files
                            </Typography>
                            <Typography variant="body2" className={className + '__empty__subtitle'}>
                                Files you've accessed recently will appear here
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}